import { useContext, useState } from "react";
import AreaChart from "src/common/charts/areaChart";
import CandleChart from "src/common/charts/candleChart";
import ChartCard from "src/common/charts/ChartCard";
import HpbChart from "src/common/charts/hpbChart";
import LineChart from "src/common/charts/lineChart";
import MomChart from "src/common/charts/momChart";
import RsiLineChart from "src/common/charts/rsiLineChart";
import YoyChart from "src/common/charts/yoyChart";
import DialogModal from "src/common/DialogModal";
import AppContext from "src/components/AppContext";
import PatternInsightSkeleton from "src/components/skeleton/PatternInsightSkeleton";
import {
  candleDetailsCardWrapperClass,
  DataPoint,
  getCandleDetailsTitle,
  getYoYReturnTrendTitle,
  renderCandleDetailsHtml,
  renderCandleDetailsReact,
} from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { useStockCharts } from "src/hooks/useStockCharts";

interface PatternInsightProps {
  togglePatterInsightModal: () => void;
  selectedCompany: string;
  insightVariant?: "portfolio" | "dataDriven";
}

const PatternInsightModal = ({
  selectedCompany,
  insightVariant = "portfolio",
}: PatternInsightProps) => {
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const user = getLocalStorageUser() || {};
  const token = user?.token;

  const isPortfolioCharts = insightVariant === "portfolio";

  const {
    chartData,
    rsiData,
    ohlcvData: lifeTimeChartData,
    yoyStockData,
    momStockData,
    hpbData: hpbStockData,
    hpbYear,
    loading,
    setHoveredCandle,
    activeCandle,
  } = useStockCharts(
    selectedCompany,
    token || "",
    true, // includeLifetime
    true, // includeMoM
    true, // includeYoY
    false, // includeOrderBlock
    undefined, // selectedDate
    false, // isDashboard
    isPortfolioCharts, // includeRsi — portfolio only
    true, // includeHpb
  );

  const [expandChart, setExpandChart] = useState<string>("");
  const [expandChartTitle, setExpandChartTitle] = useState<string>("");
  const [showModal, setShowModal] = useState(false);

  const [toggleSlot1, setToggleSlot1] = useState(false);
  const [toggleSlot2, setToggleSlot2] = useState(false);
  const [toggleSlot4, setToggleSlot4] = useState(false);

  const renderChart = (type: string, fullSize = false) => {
    switch (type) {
      case "Hpb":
        return (
          <HpbChart
            hpbChart={hpbStockData}
            darkMode={!!darkMode}
            isAnalyzeStock={true}
            isExpand={fullSize}
          />
        );
      case "MoM":
        return (
          <MomChart
            data={momStockData}
            darkMode={!!darkMode}
            isExpand={fullSize}
          />
        );
      case "YoY":
        return (
          <YoyChart
            data={yoyStockData}
            darkMode={!!darkMode}
            isExpand={fullSize}
          />
        );
      case "Area":
        return (
          <AreaChart
            areaData={lifeTimeChartData}
            darkMode={!!darkMode}
            isExpand={fullSize}
          />
        );
      case "Candle":
        return (
          <CandleChart
            candleData={chartData}
            isAnalyzeStock={true}
            symbolName={selectedCompany}
            darkMode={!!darkMode}
            isExpand={fullSize}
            onHoverChange={setHoveredCandle}
          />
        );
      case "Line":
        return (
          <LineChart
            lineData={chartData}
            symbolName={selectedCompany}
            darkMode={!!darkMode}
            isAnalyzeStock={true}
            isExpand={fullSize}
          />
        );
      case "Rsi":
        return (
          <RsiLineChart
            rsiData={rsiData}
            darkMode={!!darkMode}
            isExpand={fullSize}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <div className="flex h-full w-full flex-col overflow-y-auto bg-transparent px-1 py-1">
        {loading ? (
          <PatternInsightSkeleton />
        ) : (
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-12 lg:gap-1">
            <div className="col-span-12 lg:col-span-6">
              {isPortfolioCharts ? (
                <ChartCard
                  title={
                    <div className="flex min-w-0 flex-col gap-1.5 md:flex-row md:items-center md:gap-3">
                      <span className="shrink-0 whitespace-nowrap">
                        1 Year Chart (TF: 1 Day)
                      </span>
                      {!toggleSlot1 && chartData.length > 0 && (
                        <div
                          className={candleDetailsCardWrapperClass}
                          title={getCandleDetailsTitle(
                            activeCandle as DataPoint,
                          )}
                        >
                          {renderCandleDetailsReact(activeCandle as DataPoint)}
                        </div>
                      )}
                    </div>
                  }
                  ontoggle={true}
                  toggleChart={toggleSlot1}
                  setToggleChart={setToggleSlot1}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading && chartData.length > 0
                      ? () => {
                          setExpandChart(toggleSlot1 ? "Line" : "Candle");
                          setExpandChartTitle("1 Year Chart (TF: 1 Day)");
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading && chartData.length > 0
                    ? renderChart(toggleSlot1 ? "Line" : "Candle", false)
                    : null}
                </ChartCard>
              ) : (
                <ChartCard
                  title={`Predicting Stock Movement over each month from HpB ${hpbYear ? "since " + hpbYear : ""}`}
                  isHPB={true}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading && hpbStockData.length > 0
                      ? () => {
                          setExpandChart("Hpb");
                          setExpandChartTitle(
                            `Predicting Stock Movement over each month from HpB ${hpbYear ? "since " + hpbYear : ""}`,
                          );
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading && hpbStockData.length > 0
                    ? renderChart("Hpb")
                    : null}
                </ChartCard>
              )}
            </div>

            <div className="col-span-12 lg:col-span-6">
              {isPortfolioCharts ? (
                <ChartCard
                  title={
                    toggleSlot2 ? "RSI Trend" : "Lifetime Chart (TF: 1 Month)"
                  }
                  ontoggle={true}
                  toggleChart={toggleSlot2}
                  setToggleChart={setToggleSlot2}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading &&
                    (toggleSlot2
                      ? rsiData.length > 0
                      : lifeTimeChartData.length > 0)
                      ? () => {
                          setExpandChart(toggleSlot2 ? "Rsi" : "Area");
                          setExpandChartTitle(
                            toggleSlot2
                              ? "RSI Trend"
                              : "Lifetime Chart (TF: 1 Month)",
                          );
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading &&
                  (toggleSlot2
                    ? rsiData.length > 0
                    : lifeTimeChartData.length > 0)
                    ? renderChart(toggleSlot2 ? "Rsi" : "Area")
                    : null}
                </ChartCard>
              ) : (
                <ChartCard
                  title="Lifetime Chart (TF: 1 Month)"
                  ontoggle={false}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading && lifeTimeChartData.length > 0
                      ? () => {
                          setExpandChart("Area");
                          setExpandChartTitle("Lifetime Chart (TF: 1 Month)");
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading && lifeTimeChartData.length > 0
                    ? renderChart("Area")
                    : null}
                </ChartCard>
              )}
            </div>

            <div className="col-span-12 lg:col-span-6">
              {isPortfolioCharts ? (
                <ChartCard
                  title={`Predicting Stock Movement over each month from HpB ${hpbYear ? "since " + hpbYear : ""}`}
                  isHPB={true}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading && hpbStockData.length > 0
                      ? () => {
                          setExpandChart("Hpb");
                          setExpandChartTitle(
                            `Predicting Stock Movement over each month from HpB ${hpbYear ? "since " + hpbYear : ""}`,
                          );
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading && hpbStockData.length > 0
                    ? renderChart("Hpb")
                    : null}
                </ChartCard>
              ) : (
                <ChartCard
                  title="MoM % Return Trend"
                  ontoggle={false}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading && momStockData.length > 0
                      ? () => {
                          setExpandChart("MoM");
                          setExpandChartTitle("MoM % Return Trend");
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading && momStockData.length > 0
                    ? renderChart("MoM")
                    : null}
                </ChartCard>
              )}
            </div>

            <div className="col-span-12 lg:col-span-6">
              {isPortfolioCharts ? (
                <ChartCard
                  title={
                    toggleSlot4
                      ? "MoM % Return Trend"
                      : getYoYReturnTrendTitle()
                  }
                  ontoggle={true}
                  toggleChart={toggleSlot4}
                  setToggleChart={setToggleSlot4}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading &&
                    (toggleSlot4
                      ? momStockData.length > 0
                      : yoyStockData.length > 0)
                      ? () => {
                          setExpandChart(toggleSlot4 ? "MoM" : "YoY");
                          setExpandChartTitle(
                            toggleSlot4
                              ? "MoM % Return Trend"
                              : getYoYReturnTrendTitle(),
                          );
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading &&
                  (toggleSlot4
                    ? momStockData.length > 0
                    : yoyStockData.length > 0)
                    ? renderChart(toggleSlot4 ? "MoM" : "YoY")
                    : null}
                </ChartCard>
              ) : (
                <ChartCard
                  title={getYoYReturnTrendTitle()}
                  ontoggle={false}
                  isPatternInsightModal={true}
                  onExpand={
                    !loading && yoyStockData.length > 0
                      ? () => {
                          setExpandChart("YoY");
                          setExpandChartTitle(getYoYReturnTrendTitle());
                          setShowModal(true);
                        }
                      : undefined
                  }
                >
                  {!loading && yoyStockData.length > 0
                    ? renderChart("YoY")
                    : null}
                </ChartCard>
              )}
            </div>
          </div>
        )}
      </div>
      <DialogModal
        isChart={true}
        title={`
          <div class="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4">
            <span class="whitespace-nowrap">${selectedCompany}: ${expandChartTitle}</span>
            ${!chartData || chartData.length === 0 || expandChart !== "Candle" ? "" : renderCandleDetailsHtml(activeCandle as DataPoint)}
          </div>
        `}
        message={renderChart(expandChart, true)}
        show={showModal}
        setShow={setShowModal}
      />
    </>
  );
};

export default PatternInsightModal;
